import { AuthenticationResult, EventType, InteractionStatus } from '@azure/msal-browser'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider, useMsal } from '@azure/msal-react'
import React, { ReactNode, useEffect } from 'react'
import { msalConfig } from '../../config/msalConfig'

interface Props {
  children: ReactNode
}

const msalInstance = new PublicClientApplication(msalConfig)

/**
 * MsalProvider calls initialize() and handleRedirectPromise() itself. This wrapper
 * used to call both as well, and the two raced for the redirect response: whichever
 * lost read an already-cleared hash and threw hash_empty_error. msal-react swallows
 * its own copy of that failure, so only the duplicate call reported it.
 *
 * The active account is set from sign-in events instead, which is what those calls
 * were for.
 */
msalInstance.addEventCallback((event) => {
  const account = (event.payload as AuthenticationResult | null)?.account
  if (!account) return

  if (
    event.eventType === EventType.LOGIN_SUCCESS ||
    event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
    event.eventType === EventType.SSO_SILENT_SUCCESS
  ) {
    msalInstance.setActiveAccount(account)
  }
})

/**
 * Covers the case no event fires for: a page load where the browser already holds a
 * signed-in account from a previous visit.
 */
const ActiveAccountSync = () => {
  const { instance, accounts, inProgress } = useMsal()

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return
    if (!instance.getActiveAccount() && accounts.length > 0) {
      instance.setActiveAccount(accounts[0])
    }
  }, [instance, accounts, inProgress])

  return null
}

const MsalProviderCustom = ({ children }: Props) => (
  <MsalProvider instance={msalInstance}>
    <ActiveAccountSync />
    {children}
  </MsalProvider>
)

export default MsalProviderCustom
